const PI = Math.PI;
const X_PI = PI * 3000.0 / 180.0;
const AXIS = 6378245.0;
const EE = 0.00669342162296594323;

const COORDINATE_ALIASES = {
  BD09: 'BD09',
  'BD-09': 'BD09',
  GCJ02: 'GCJ02',
  'GCJ-02': 'GCJ02',
  MARS: 'GCJ02',
  WGS84: 'WGS84',
  'WGS-84': 'WGS84',
  EPSG4326: 'WGS84'
};

function normalizeCoordinateSystem (coordinateSystem) {
  const value = String(coordinateSystem || '').trim().toUpperCase();
  return COORDINATE_ALIASES[value] || value;
}

function isValidPoint (point) {
  if (!point) return false;
  // 先拒绝缺失值，避免 Number(null) 和 Number('') 将未填写坐标变成零。
  if ([point.lng, point.lat].some(value =>
    (typeof value !== 'number' && typeof value !== 'string') || String(value).trim() === ''
  )) return false;
  const lng = Number(point.lng);
  const lat = Number(point.lat);
  // 业务接口用 (0, 0) 表示未配置坐标，不能参与视野计算或创建 Marker。
  return Number.isFinite(lng) && Number.isFinite(lat) &&
    Math.abs(lng) <= 180 && Math.abs(lat) <= 90 && !(lng === 0 && lat === 0);
}

function isOutsideChina (lng, lat) {
  return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
}

function transformLatitude (lng, lat) {
  let result = -100.0 + 2.0 * lng + 3.0 * lat + 0.2 * lat * lat + 0.1 * lng * lat + 0.2 * Math.sqrt(Math.abs(lng));
  result += (20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0 / 3.0;
  result += (20.0 * Math.sin(lat * PI) + 40.0 * Math.sin(lat / 3.0 * PI)) * 2.0 / 3.0;
  result += (160.0 * Math.sin(lat / 12.0 * PI) + 320 * Math.sin(lat * PI / 30.0)) * 2.0 / 3.0;
  return result;
}

function transformLongitude (lng, lat) {
  let result = 300.0 + lng + 2.0 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * Math.sqrt(Math.abs(lng));
  result += (20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0 / 3.0;
  result += (20.0 * Math.sin(lng * PI) + 40.0 * Math.sin(lng / 3.0 * PI)) * 2.0 / 3.0;
  result += (150.0 * Math.sin(lng / 12.0 * PI) + 300.0 * Math.sin(lng / 30.0 * PI)) * 2.0 / 3.0;
  return result;
}

function wgs84ToGcj02 (point) {
  const lng = Number(point.lng);
  const lat = Number(point.lat);
  if (isOutsideChina(lng, lat)) return { lng, lat };

  const dLat = transformLatitude(lng - 105.0, lat - 35.0);
  const dLng = transformLongitude(lng - 105.0, lat - 35.0);
  const radLat = lat / 180.0 * PI;
  let magic = Math.sin(radLat);
  magic = 1 - EE * magic * magic;
  const sqrtMagic = Math.sqrt(magic);
  const latitude = lat + (dLat * 180.0) / ((AXIS * (1 - EE)) / (magic * sqrtMagic) * PI);
  const longitude = lng + (dLng * 180.0) / (AXIS / sqrtMagic * Math.cos(radLat) * PI);
  return { lng: longitude, lat: latitude };
}

function gcj02ToWgs84 (point) {
  const lng = Number(point.lng);
  const lat = Number(point.lat);
  if (isOutsideChina(lng, lat)) return { lng, lat };
  let result = { lng, lat };
  // 迭代消除正向公式的残差，降低一次反推误差；阈值不代表现场定位精度。
  for (let i = 0; i < 10; i++) {
    const converted = wgs84ToGcj02(result);
    const dLng = converted.lng - lng;
    const dLat = converted.lat - lat;
    result = { lng: result.lng - dLng, lat: result.lat - dLat };
    if (Math.max(Math.abs(dLng), Math.abs(dLat)) < 1e-7) break;
  }
  return result;
}

function gcj02ToBd09 (point) {
  const lng = Number(point.lng);
  const lat = Number(point.lat);
  const z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(lat * X_PI);
  const theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(lng * X_PI);
  return {
    lng: z * Math.cos(theta) + 0.0065,
    lat: z * Math.sin(theta) + 0.006
  };
}

function bd09ToGcj02 (point) {
  const lng = Number(point.lng) - 0.0065;
  const lat = Number(point.lat) - 0.006;
  const z = Math.sqrt(lng * lng + lat * lat) - 0.00002 * Math.sin(lat * X_PI);
  const theta = Math.atan2(lat, lng) - 0.000003 * Math.cos(lng * X_PI);
  return {
    lng: z * Math.cos(theta),
    lat: z * Math.sin(theta)
  };
}

export function getMapCoordinateSource () {
  return normalizeCoordinateSystem(process.env.VUE_APP_CTX_MAP_COORDINATE_SOURCE || 'BD09');
}

export function getTiandituCoordinateSystem () {
  return normalizeCoordinateSystem(process.env.VUE_APP_CTX_TIANDITU_COORDINATE_SYSTEM || 'WGS84');
}

export function convertCoordinate (point, source = getMapCoordinateSource(), target = getTiandituCoordinateSystem()) {
  if (!isValidPoint(point)) return null;

  const normalizedSource = normalizeCoordinateSystem(source);
  const normalizedTarget = normalizeCoordinateSystem(target);
  if (normalizedSource === normalizedTarget) {
    return { lng: Number(point.lng), lat: Number(point.lat) };
  }
  // 以 GCJ02 为中间坐标，BD09 与 GCJ02 互转不再绕行 WGS84。
  let gcj02Point;
  switch (normalizedSource) {
    case 'WGS84':
      gcj02Point = wgs84ToGcj02(point);
      break;
    case 'GCJ02':
      gcj02Point = { lng: Number(point.lng), lat: Number(point.lat) };
      break;
    case 'BD09':
      gcj02Point = bd09ToGcj02(point);
      break;
    default:
      throw new Error(`[mapCoordinate] 不支持的源坐标系: ${source}`);
  }

  switch (normalizedTarget) {
    case 'WGS84':
      return gcj02ToWgs84(gcj02Point);
    case 'GCJ02':
      return gcj02Point;
    case 'BD09':
      return gcj02ToBd09(gcj02Point);
    default:
      throw new Error(`[mapCoordinate] 不支持的目标坐标系: ${target}`);
  }
}

// 普通底图展示使用 WGS84 近似经纬度，不代表严格的 CGCS2000 基准转换。
// 固定中心点可显式指定来源；业务点仍采用统一的后端坐标配置。
export function convertToTianditu (point, source = getMapCoordinateSource()) {
  return convertCoordinate(point, source, getTiandituCoordinateSystem());
}

export function convertPointListToTianditu (pointList = []) {
  return pointList.map(point => convertToTianditu(point)).filter(Boolean);
}
